/* eslint-disable react/no-unknown-property */
import React, { useRef, useEffect, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'

type LorenzAttractorProps = {
  dt?: number
  sigma?: number
  rho?: number
  beta?: number
  numSteps?: number
  speed?: number
  color?: string
  initialConditions?: [number, number, number]
}

const LorenzAttractor: React.FC<LorenzAttractorProps> = ({
  dt = 0.005,
  sigma = 10,
  rho = 28,
  beta = 8 / 3,
  numSteps = 50000,
  speed = 5,
  color,
  initialConditions,
}) => {
  const lineRef = useRef<THREE.Line>(null)
  const pointRefs = useRef<(THREE.Mesh | null)[]>([])
  const [points, setPoints] = useState<THREE.Vector3[]>([])
  const [maxPoints, setMaxPoints] = useState<number>(0)
  const bigPointRef = useRef<THREE.Mesh | null>(null)

  const lorenz = (x: number, y: number, z: number) => {
    const xDot = sigma * (y - x)
    const yDot = x * (rho - z) - y
    const zDot = x * y - beta * z
    return [xDot, yDot, zDot]
  }

  useEffect(() => {
    const newPoints: THREE.Vector3[] = []
    let [x, y, z] = initialConditions || [0.1, 0, 0]
    for (let i = 0; i < numSteps; i++) {
      const [xDot, yDot, zDot] = lorenz(x, y, z)
      x += xDot! * dt
      y += yDot! * dt
      z += zDot! * dt
      newPoints.push(new THREE.Vector3(x, y, z))
    }
    setPoints(newPoints)
    setMaxPoints(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dt, sigma, rho, beta, numSteps, initialConditions])

  useFrame(() => {
    if (maxPoints < points.length) {
      const geometry = new THREE.BufferGeometry().setFromPoints(
        points.slice(0, maxPoints + speed),
      )
      if (lineRef.current) {
        lineRef.current.geometry.dispose()
        lineRef.current.geometry = geometry
      }
      setMaxPoints(prev => prev + speed)
    }

    if (bigPointRef.current && maxPoints > 0) {
      const headPosition = points[maxPoints - 1]!
      const worldHeadPosition = headPosition
        .clone()
        .applyMatrix4(lineRef.current!.matrixWorld)
      bigPointRef.current.position.copy(worldHeadPosition)
    }

    if (lineRef.current) {
      const time = performance.now() * 0.001 // Time-based rotation
      lineRef.current.rotation.x = Math.sin(time * 0.3) * 0.5
      lineRef.current.rotation.y = time * 0.5
      lineRef.current.rotation.z = Math.cos(time * 0.2) * 0.5
    }

    pointRefs.current.forEach((pointRef, index) => {
      if (pointRef) {
        pointRef.position.copy(points[index]!) // Update position of each highlight point
        // Set gradient color based on the index of the point
        const colorGradient = new THREE.Color()
        colorGradient.setHSL(index / points.length, 1, 0.5) // Gradient color based on position
        if (pointRef.material instanceof THREE.MeshBasicMaterial) {
          pointRef.material.color = colorGradient // Apply the color to the material
        }
      }
    })
  })

  return (
    <>
      {/* @ts-expect-error typescript issue with threejs */}
      <line ref={lineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={color} />
      </line>

      {points.slice(0, maxPoints).map((point, index) => {
        if (index < 10) {
          return (
            <mesh
              key={index}
              ref={el => (pointRefs.current[index] = el)}
              position={point}
            >
              <meshBasicMaterial
                color={new THREE.Color().setHSL(index / 0, 1, 0.5)}
              />
            </mesh>
          )
        }
        return null
      })}

      <mesh ref={bigPointRef}>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>
    </>
  )
}

let isHydrating = true

export const LorenzAttractorWrapper: React.FC = () => {
  const [isHydrated, setIsHydrated] = React.useState(!isHydrating)

  useEffect(() => {
    isHydrating = false
    setIsHydrated(true)
  }, [])

  if (!isHydrated) {
    return null
  }

  return (
    <Canvas camera={{ position: [20, 20, 30] }}>
      <LorenzAttractor sigma={28} rho={46.92} beta={4} color="#475569" />
      <LorenzAttractor sigma={10} rho={28} beta={8 / 3} color="#475569" />
    </Canvas>
  )
}
